import { Box, useTheme } from "@mui/material";
import React from "react";
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
} from "recharts";

interface CriteriaRadarChartProps {
  criteriaResults: Record<
    string,
    {
      score: number;
      label?: string;
    }
  >;
  criteria?: Array<{ id: string; label: string }>;
}

const CustomTick = ({ x, y, payload, fill, cx, cy }: any) => {
  const maxLength = 20;
  const text = payload.value;

  // Determine text anchor based on position relative to center
  const dx = x - cx;
  const dy = y - cy;
  const angle = Math.atan2(dy, dx);
  const angleDeg = (angle * 180) / Math.PI;

  // Use start for right side, end for left side, middle for top/bottom
  let textAnchor: "start" | "middle" | "end" = "middle";
  let yOffset = 0;

  if (angleDeg > -45 && angleDeg < 45) {
    textAnchor = "start"; // Right side
  } else if (angleDeg > 135 || angleDeg < -135) {
    textAnchor = "end"; // Left side
  } else if (angleDeg < -45 && angleDeg > -135) {
    yOffset = -10; // Top side - move text up
  }

  if (text.length <= maxLength) {
    return (
      <text
        x={x}
        y={y + yOffset}
        fill={fill}
        fontSize={11}
        textAnchor={textAnchor}
      >
        {text}
      </text>
    );
  }

  const words = text.split(" ");
  const lines: string[] = [];
  let currentLine = "";

  words.forEach((word: string) => {
    if ((currentLine + " " + word).length <= maxLength) {
      currentLine = currentLine ? currentLine + " " + word : word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);

  const displayLines = lines.slice(0, 2);
  if (lines.length > 2) {
    displayLines[1] = displayLines[1] + "...";
  }

  return (
    <text
      x={x}
      y={y + yOffset}
      fill={fill}
      fontSize={11}
      textAnchor={textAnchor}
    >
      {displayLines.map((line, index) => (
        <tspan key={index} x={x} dy={index === 0 ? 0 : 12}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

const CriteriaRadarChart: React.FC<CriteriaRadarChartProps> = ({
  criteriaResults,
  criteria,
}) => {
  const theme = useTheme();

  const chartData = criteria
    ? [
        ...criteria
          .filter((c) => criteriaResults[c.id])
          .map((c) => ({
            criterion: c.label,
            score: criteriaResults[c.id].score,
            fullMark: 5,
          })),
        ...Object.entries(criteriaResults)
          .filter(([id]) => !criteria.some((c) => c.id === id))
          .map(([criterionId, result]) => ({
            criterion:
              result.label || `Criterion ${criterionId.substring(0, 8)}`,
            score: result.score,
            fullMark: 5,
          })),
      ]
    : Object.entries(criteriaResults).map(([criterionId, result]) => ({
        criterion: result.label || `Criterion ${criterionId.substring(0, 8)}`,
        score: result.score,
        fullMark: 5,
      }));

  return (
    <Box sx={{ width: "100%", height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={chartData}>
          {/* @ts-ignore - Recharts type compatibility with React 18 */}
          <PolarGrid stroke={theme.palette.divider} strokeDasharray="3 3" />
          {/* @ts-ignore - Recharts type compatibility with React 18 */}
          <PolarAngleAxis
            dataKey="criterion"
            tick={<CustomTick fill={theme.palette.text.secondary} />}
          />
          {/* @ts-ignore - Recharts type compatibility with React 18 */}
          <PolarRadiusAxis
            angle={90}
            domain={[0, 5]}
            tick={false}
            axisLine={false}
          />
          {/* @ts-ignore - Recharts type compatibility with React 18 */}
          <Radar
            name="Score"
            dataKey="score"
            stroke={theme.palette.primary.main}
            fill={theme.palette.primary.main}
            fillOpacity={0.3}
            strokeWidth={2}
          />
        </RadarChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default CriteriaRadarChart;
